// Pure logic behind the method editor: the editable draft, the checks run
// before saving and the payload. No React, no network — covered by
// methodDraft.test.ts. The server (recipe_cards.go) re-checks everything here.

import { RC } from './recipeCardStrings'
import type { RecipeMethod, RecipeMethodPayload } from './recipeCardTypes'

/** Mirrors maxRecipeSteps / maxRecipeStepLength in recipe_cards.go. */
export const MAX_STEPS = 40
export const MAX_STEP_LENGTH = 600

/** The units a yield can be stated in — every convertible family. */
export const YIELD_UNITS = ['kg', 'g', 'liter', 'ml', 'each', 'dozen', 'lb', 'oz', 'pair'] as const

export interface StepDraft {
  key: string
  text: string
}

export interface MethodDraft {
  yieldQty: string
  /** '' = not stated. */
  yieldUnit: string
  steps: StepDraft[]
}

let keySeq = 0
export function newStep(text = ''): StepDraft {
  keySeq += 1
  return { key: `st-${keySeq}`, text }
}

export function stepsFromServer(steps: string[] | null | undefined): StepDraft[] {
  return (steps ?? []).map((s) => newStep(s))
}

export function draftFromMethod(m: Pick<RecipeMethod, 'recipe_yield_qty' | 'recipe_yield_unit' | 'steps'> | undefined): MethodDraft {
  if (!m) return { yieldQty: '', yieldUnit: '', steps: [] }
  return {
    yieldQty: m.recipe_yield_qty == null ? '' : String(m.recipe_yield_qty),
    yieldUnit: m.recipe_yield_unit ?? '',
    steps: stepsFromServer(m.steps),
  }
}

/** The steps as they will be saved: trimmed, blanks dropped. */
export function cleanSteps(steps: StepDraft[]): string[] {
  return steps.map((s) => s.text.replace(/\r\n/g, '\n').trim()).filter((s) => s !== '')
}

/** Two step lists are the same method (ignoring row keys and blank rows). */
export function sameSteps(a: StepDraft[], b: StepDraft[]): boolean {
  return JSON.stringify(cleanSteps(a)) === JSON.stringify(cleanSteps(b))
}

export function sameMethodDraft(a: MethodDraft, b: MethodDraft): boolean {
  const y = (d: MethodDraft) => (d.yieldUnit === '' ? null : [Number(d.yieldQty) || 0, d.yieldUnit])
  return JSON.stringify(y(a)) === JSON.stringify(y(b)) && sameSteps(a.steps, b.steps)
}

export function moveStep(steps: StepDraft[], key: string, by: -1 | 1): StepDraft[] {
  const i = steps.findIndex((s) => s.key === key)
  const j = i + by
  if (i < 0 || j < 0 || j >= steps.length) return steps
  const out = steps.slice()
  ;[out[i], out[j]] = [out[j], out[i]]
  return out
}

/** Problems with the steps alone, in plain words. Empty = ready to save. */
export function stepProblems(steps: StepDraft[]): string[] {
  const out: string[] = []
  const clean = cleanSteps(steps)
  clean.forEach((s, i) => {
    // Counted in characters, not UTF-16 units, as the server does.
    if ([...s].length > MAX_STEP_LENGTH) out.push(RC.stepTooLong(i + 1, MAX_STEP_LENGTH))
  })
  if (clean.length > MAX_STEPS) out.push(RC.tooManySteps(MAX_STEPS))
  return out
}

export function methodProblems(d: MethodDraft): string[] {
  const out: string[] = []
  if (d.yieldUnit !== '') {
    const q = Number(d.yieldQty)
    if (d.yieldQty.trim() === '' || !Number.isFinite(q) || q <= 0) out.push(RC.yieldNeedsQty)
  } else if (d.yieldQty.trim() !== '' && Number(d.yieldQty) > 0) {
    out.push(RC.yieldNeedsUnit)
  }
  return [...out, ...stepProblems(d.steps)]
}

export function toMethodPayload(d: MethodDraft): RecipeMethodPayload {
  const stated = d.yieldUnit !== ''
  return {
    recipe_yield_qty: stated ? Number(d.yieldQty) : null,
    recipe_yield_unit: stated ? d.yieldUnit : '',
    steps: cleanSteps(d.steps),
  }
}

/** What one serving comes to, for the hint under the yield; null when it cannot be said. */
export function perServing(d: MethodDraft, servings: number): { qty: number; unit: string } | null {
  const q = Number(d.yieldQty)
  if (d.yieldUnit === '' || !Number.isFinite(q) || q <= 0 || !(servings > 0)) return null
  return { qty: Math.round((q / servings) * 1e5) / 1e5, unit: d.yieldUnit }
}
